"use client";
import React, { createContext, useState, useEffect } from "react";
import { BASE_URL, UPLOAD_URL } from "../config/apiConfig";

// Create the context
export const OrdersContext = createContext();

// Provider
export const OrdersProvider = ({ children }) => {
  const [ongoingOrders, setOngoingOrders] = useState([]);
  const [deliveredOrders, setDeliveredOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // =========================
  // FETCH ORDERS
  // =========================
  const fetchOrders = async () => {
    const userId = localStorage.getItem("user_id");
    const token = localStorage.getItem("auth_token");

    if (!userId || !token) {
      setOngoingOrders([]);
      setDeliveredOrders([]);
      setError("Missing user credentials. Please log in.");
      setLoading(false);
      return;
    }

    try {
      setLoading(true);

      const response = await fetch(`${BASE_URL}/orders/${userId}`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const text = await response.text();
        throw new Error(`HTTP ${response.status}: ${text}`);
      }

      const result = await response.json();

      if (result.status === 200 && Array.isArray(result.data)) {
        const ongoing = [];
        const delivered = [];

        result.data.forEach((order) => {
          const formattedOrder = {
            id: `#ORD${order.order_id}`,
            items: Array.isArray(order.products)
              ? order.products.map((product) => ({
                  name: product.product_name,
                  quantity: Number(product.quantity),
                  image: `${UPLOAD_URL}/products/${product.pimage}`,
                }))
              : [],
            status: capitalize(order.order_status),
            orderDate: `Ordered on ${formatDateTime(
              order.create_audit_time
            )}`,
          };

          if (order.order_status === "delivered") {
            formattedOrder.deliveredDate = formatDate(
              order.delivered_audit_time
            );
            delivered.push(formattedOrder);
          } else if (order.order_status === "returned") {
            formattedOrder.returnedDate = formatDate(
              order.returnedDate || order.delivered_audit_time
            );
            delivered.push(formattedOrder);
          } else {
            ongoing.push(formattedOrder);
          }
        });

        setOngoingOrders(ongoing);
        setDeliveredOrders(delivered);
        setError(null);
      } else {
        throw new Error("Invalid API response");
      }
    } catch (err) {
      console.error("Fetch Orders Error:", err);
      setError(err.message || "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  // =========================
  // INITIAL LOAD
  // =========================
  useEffect(() => {
    fetchOrders();
  }, []);

  // =========================
  // AUTO REFRESH ON TAB / NAVIGATION
  // =========================
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        fetchOrders();
      }
    };

    const handleStorageChange = () => {
      fetchOrders();
    };

    document.addEventListener(
      "visibilitychange",
      handleVisibilityChange
    );
    window.addEventListener("storage", handleStorageChange);

    return () => {
      document.removeEventListener(
        "visibilitychange",
        handleVisibilityChange
      );
      window.removeEventListener("storage", handleStorageChange);
    };
  }, []);

  // =========================
  // PLACE ORDER
  // =========================
  const placeOrder = async (orderData) => {
    const token = localStorage.getItem("auth_token");
    if (!token)
      return { status: false, error: "User not authenticated" };

    const response = await fetch(`${BASE_URL}/place-order`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(orderData),
    });

    const result = await response.json();

    if (response.status === 403) {
      return {
        status: false,
        error:
          result.messages?.error ||
          "You already have an active order.",
      };
    }

    if (!result.status) {
      return {
        status: false,
        error: result.message || "Failed to place order",
      };
    }

    // 🔥 auto refresh orders after placing
    fetchOrders();

    return { status: true, order_id: result.order_id };
  };

  // =========================
  // HELPERS
  // =========================
  const formatDateTime = (dateTimeStr) => {
    if (!dateTimeStr) return "";
    const date = new Date(dateTimeStr);
    return date.toLocaleString("en-US", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  const formatDate = (dateTimeStr) => {
    if (!dateTimeStr) return "";
    const date = new Date(dateTimeStr);
    return date.toLocaleDateString("en-US", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const capitalize = (str = "") =>
    str ? str.charAt(0).toUpperCase() + str.slice(1) : "";

  // =========================
  // PROVIDER
  // =========================
  return (
    <OrdersContext.Provider
      value={{
        ongoingOrders,
        deliveredOrders,
        loading,
        error,
        refreshOrders: fetchOrders,
        placeOrder,
      }}
    >
      {children}
    </OrdersContext.Provider>
  );
};
